import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Music, BookOpen, Gamepad2, Target, Mail, CheckCircle, Sparkles } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const StartHubPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'start-hub', name: '' })
      });
      if (res.ok) setSubscribed(true);
    } catch {
      setSubscribed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const ecosystems = [
    {
      id: 'powermoves',
      title: 'Power Moves',
      subtitle: 'The Life Skills We Were Never Taught',
      description: 'A full curriculum system designed to teach financial literacy, entrepreneurship, and real-world life skills.',
      image: 'https://customer-assets.emergentagent.com/job_eblack-store/artifacts/24w6xjnt_POWER%20MOVES%20front%20cover.png',
      icon: Zap,
      color: 'from-amber-600 to-yellow-500',
      accent: 'text-amber-400',
      border: 'border-amber-600/30 hover:border-amber-500/60',
      path: '/powermoves',
      badge: 'COMING SOON',
    },
    {
      id: 'mindsetshift',
      title: 'Mindset Shift',
      subtitle: 'Transformational Media & Music',
      description: 'The official soundtrack, book series, video content, and corporate licensing — all designed to shift your perspective.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png',
      icon: Music,
      color: 'from-purple-600 to-pink-600',
      accent: 'text-purple-400',
      border: 'border-purple-600/30 hover:border-purple-500/60',
      path: '/mindsetshift',
      badge: null,
    },
    {
      id: 'thisorthat',
      title: 'THIS or THAT',
      subtitle: 'The Interactive Book Series',
      description: 'The conversation-starting game book series. Multiple volumes for couples, families, kids, and game night.',
      image: 'https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/dezhjave_1%20THIS%20or%20THAT%20How%20Well%20Do%20You%20Know%20Them%20Vol%201.%20cover.png',
      icon: Gamepad2,
      color: 'from-red-600 to-rose-600',
      accent: 'text-red-400',
      border: 'border-red-600/30 hover:border-red-500/60',
      path: '/thisorthat',
      badge: null,
    },
    {
      id: 'authors',
      title: 'Author eCoSystem',
      subtitle: '400+ Marketing Platforms for Authors',
      description: 'The Ultimate Author Social Media eCoSystem — comprehensive marketing infrastructure to grow your readership.',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png',
      icon: BookOpen,
      color: 'from-green-600 to-emerald-600',
      accent: 'text-green-400',
      border: 'border-green-600/30 hover:border-green-500/60',
      path: '/authors',
      badge: 'BESTSELLER',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/20 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/67pzcjdk_eBlackBookListings.png"
            alt="eBlackBookListings"
            className="h-16 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="start-hub-title">
            Welcome to the <span className="text-amber-400">eCoSystem</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-4">
            eBlackBookListings LLC builds media, scales authors, and engineers growth through transformational content and digital infrastructure.
          </p>
          <div className="flex items-center justify-center gap-2 text-amber-400 font-semibold mb-10">
            <Sparkles className="w-5 h-5" />
            <span>Choose Your Starting Point</span>
            <Sparkles className="w-5 h-5" />
          </div>
        </div>
      </section>

      {/* Ecosystem Grid */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {ecosystems.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className={`group bg-gray-900/50 border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer ${item.border}`}
                  onClick={() => navigate(item.path)}
                  data-testid={`ecosystem-card-${item.id}`}
                >
                  <div className="grid grid-cols-5 gap-0">
                    {/* Image */}
                    <div className="col-span-2 relative">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover min-h-[240px]" />
                      {item.badge && (
                        <div className={`absolute top-3 left-3 ${item.badge === 'COMING SOON' ? 'bg-amber-600' : 'bg-green-600'} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                          {item.badge}
                        </div>
                      )}
                    </div>
                    {/* Content */}
                    <div className="col-span-3 p-6 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors">{item.title}</h3>
                      </div>
                      <p className={`text-sm font-semibold ${item.accent} mb-2`}>{item.subtitle}</p>
                      <p className="text-gray-400 text-sm mb-4 leading-relaxed">{item.description}</p>
                      <Button className={`w-fit bg-gradient-to-r ${item.color} text-white font-semibold text-sm`}>
                        Explore
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Email Capture */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Target className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-3">Stay Connected</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Get exclusive updates on new products, launches, and early access opportunities.
          </p>
          {subscribed ? (
            <div className="flex items-center justify-center gap-2 text-green-400 font-semibold" data-testid="subscribed-msg">
              <CheckCircle className="w-5 h-5" />
              <span>You're on the list! Check your email for confirmation.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="bg-black border-gray-700 text-white flex-1"
                data-testid="email-capture-input"
              />
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold" data-testid="email-capture-btn">
                {submitting ? 'Joining...' : 'Join the List'}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate('/store')} variant="outline" className="border-gray-700 text-gray-300 hover:border-amber-500 hover:text-amber-400">
              Visit Store
            </Button>
            <Button onClick={() => navigate('/portfolio')} variant="outline" className="border-gray-700 text-gray-300 hover:border-amber-500 hover:text-amber-400">
              View Portfolio
            </Button>
            <Button onClick={() => navigate('/start-project')} className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              Start a Project
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StartHubPage;
